/**
 * Campaign soft delete (migration 2026-10-04-203).
 *
 * Owner decision 2026-10-04: admins and superadmins can delete (hide) campaigns they chose not to send. The database
 * is the authority (RPC soft_delete_campaign refuses anything else); these helpers only decide what the UI shows.
 */

/** Visible = not soft-deleted. NULL / undefined / false all count as visible (is_deleted is a nullable column). */
export function isCampaignVisible(c: { is_deleted?: boolean | null }): boolean {
  return c.is_deleted !== true;
}

/** The same rule for a list. Every campaign list also filters server-side; this is the belt to that brace. */
export function visibleCampaigns<T extends { is_deleted?: boolean | null }>(
  rows: T[] | null | undefined,
): T[] {
  return (rows ?? []).filter(isCampaignVisible);
}

/** Roles spell differently across the app ('superadmin' in the DB, 'super_admin' in the router). */
export function canSoftDeleteCampaigns(
  role: string | null | undefined,
): boolean {
  const r = (role ?? "").toLowerCase().replace(/_/g, "");
  return r === "admin" || r === "superadmin";
}

/** Flight statuses that still count as "never sent" — the same set the RPC accepts. */
export const UNSENT_FLIGHT_STATUSES = ["draft", "cancelled"] as const;

export function isCampaignUnsent(
  flightStatuses: ReadonlyArray<string | null | undefined>,
): boolean {
  return flightStatuses.every(
    (s) =>
      typeof s === "string" &&
      (UNSENT_FLIGHT_STATUSES as readonly string[]).includes(s),
  );
}

export type CampaignDeleteButtonState =
  /** admin + unsent: the button works */
  | "enabled"
  /** admin, but a flight was scheduled/sent: shown disabled with an explanation */
  | "disabled_sent"
  /** not an admin, or already deleted: no button */
  | "hidden";

export function campaignDeleteButtonState(
  role: string | null | undefined,
  campaign: { is_deleted?: boolean | null },
  flightStatuses: ReadonlyArray<string | null | undefined>,
): CampaignDeleteButtonState {
  if (!canSoftDeleteCampaigns(role) || !isCampaignVisible(campaign)) {
    return "hidden";
  }
  return isCampaignUnsent(flightStatuses) ? "enabled" : "disabled_sent";
}

export const CAMPAIGN_DELETE_SENT_TOOLTIP =
  "Only campaigns that were never scheduled or sent can be deleted. This campaign has a flight that was scheduled or sent.";

export const CAMPAIGN_DELETE_CONFIRM = (name: string): string =>
  `Delete campaign "${name}"?\n\n` +
  "The campaign and its draft flights will be hidden from every campaign list. Nothing is erased: its history is kept, " +
  "and the audience lists it used are not affected and can still be edited or deleted.\n\n" +
  "Only an admin can undo this, through support.";

/** Operator-facing text for a refusal from soft_delete_campaign (or the guards around it). */
export function campaignDeleteErrorMessage(error: unknown): string {
  const msg =
    typeof error === "object" && error && "message" in error
      ? String((error as { message: unknown }).message)
      : String(error ?? "");
  if (msg.includes("CAMPAIGN_DELETE_ADMIN_ONLY")) {
    return "Only an admin or superadmin can delete a campaign.";
  }
  if (msg.includes("ACTIVE_PROFILE_REQUIRED")) {
    return "Your account is not active, so you cannot delete campaigns.";
  }
  if (msg.includes("CAMPAIGN_DELETE_ALREADY_SENT")) {
    return "This campaign can't be deleted because one of its flights was scheduled or sent. Only campaigns that never sent can be deleted.";
  }
  if (msg.includes("CAMPAIGN_DELETE_EIB_INVITATION")) {
    return "This campaign holds an Event in a Box invitation. Cancel the invitation instead of deleting the campaign.";
  }
  if (msg.includes("CAMPAIGN_NOT_FOUND")) {
    return "This campaign no longer exists.";
  }
  return msg
    ? `Could not delete the campaign: ${msg}`
    : "Could not delete the campaign.";
}
